// # Product Analysis Component for Net Revenue Dashboard
import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line
} from 'recharts';
import { 
  FaChartPie, 
  FaChartBar,
  FaFilter,
  FaMoneyBillWave,
  FaPercentage,
  FaDollarSign,
  FaShoppingCart,
  FaUsers
} from 'react-icons/fa';
import './net_product_analysis.css';

// # Product type configurations
const PRODUCT_TYPES = {
  step1_library: {
    label: 'Step 1 Library Access',
    category: 'library'
  },
  step2_library: {
    label: 'Step 2 Library Access',
    category: 'library'
  },
  step1_mentorship: {
    label: 'Step 1 Mentorship',
    category: 'mentorship'
  },
  step2_mentorship: {
    label: 'Step 2 Mentorship',
    category: 'mentorship'
  },
  step1_premium: {
    label: 'Step 1 Premium',
    category: 'premium'
  },
  step2_premium: {
    label: 'Step 2 Premium',
    category: 'premium'
  },
  step1_mentorship_premium: {
    label: 'Step 1 Mentorship Premium',
    category: 'premium'
  },
  step2_mentorship_premium: {
    label: 'Step 2 Mentorship Premium',
    category: 'premium'
  },
  OET: {
    label: 'OET',
    category: 'mentorship'
  },
  step1_tutor_hosting: {
    label: 'Step 1 Tutor Hosting',
    category: 'tutor'
  },
  step2_tutor_hosting: {
    label: 'Step 2 Tutor Hosting',
    category: 'tutor'
  },
  US_VISA_Counselling: {
    label: 'US VISA Counselling',
    category: 'mentorship'
  }
};

// # Update formatProductName helper
const formatProductName = (productKey) => {
  return PRODUCT_TYPES[productKey]?.label || productKey;
};

// # Update the default data structure
const defaultAnalyticsData = {
  revenueOverTime: [],
  stats: {
    totalRevenue: 0
  }
};

const ProductAnalysis = ({ analyticsData = defaultAnalyticsData }) => {
  // # State for product filtering
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [filteredData, setFilteredData] = useState({
    revenueByProduct: [],
    netRevenueByProduct: [],
    currencyDistribution: []
  });
  
  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
  
  // # Calculate product-specific stats
  const [productStats, setProductStats] = useState({
    totalProducts: 0,
    topProduct: '',
    topProductRevenue: 0,
    averageRevenue: 0,
    totalTransactions: 0
  });
  
  // # Process analytics data to create revenueByProduct
  useEffect(() => {
    if (!analyticsData?.rawPayments?.length) return;

    // # Transform raw payments data into product-based structure
    const productRevenue = {};
    
    // # First, identify all product keys in the data
    const productKeys = new Set();
    analyticsData.rawPayments.forEach(payment => {
      if (payment.product && PRODUCT_TYPES[payment.product]) {
        productKeys.add(payment.product);
      }
    });
    
    // # Initialize product revenue tracking
    productKeys.forEach(key => {
      productRevenue[key] = {
        product: key,
        productLabel: PRODUCT_TYPES[key]?.label || key,
        category: PRODUCT_TYPES[key]?.category || 'other',
        total: 0,
        net: 0,
        budget: 0,
        count: 0,
        customers: new Set()
      };
    });
    
    // # Process each payment
    analyticsData.rawPayments.forEach(payment => {
      const product = payment.product;
      if (product && productRevenue[product]) {
        productRevenue[product].total += payment.egp_total || 0;
        productRevenue[product].net += payment.egp_net || 0;
        productRevenue[product].budget += payment.budget_dvd_amount || 0;
        productRevenue[product].count++;
        
        if (payment.email) {
          productRevenue[product].customers.add(payment.email);
        }
      }
    });

    // # Update filtered data with processed product revenue
    setFilteredData({
      revenueByProduct: Object.values(productRevenue),
      netRevenueByProduct: Object.values(productRevenue).map(product => ({
        product: product.productLabel,
        amount: product.net
      })),
      currencyDistribution: [] // Add if you have this data
    });
    
    // # Update product stats
    // # Find top product by revenue
    let topProduct = '';
    let topProductRevenue = 0;
    
    Object.values(productRevenue).forEach(product => {
      if (product.total > topProductRevenue) {
        topProductRevenue = product.total;
        topProduct = product.productLabel;
      }
    });
    
    // # Update product stats
    setProductStats({
      totalProducts: Object.keys(productRevenue).length,
      topProduct,
      topProductRevenue,
      totalTransactions: analyticsData.rawPayments.length,
      averageRevenue: analyticsData.rawPayments.length > 0 ? 
        Object.values(productRevenue).reduce((sum, product) => sum + product.total, 0) / analyticsData.rawPayments.length : 0
    });

  }, [analyticsData]);
  
  // # Handle product selection change
  const handleProductChange = (e) => {
    setSelectedProduct(e.target.value);
  };
  
  // # Format currency values
  const formatCurrency = (value) => {
    return `E£ ${value.toLocaleString()}`;
  };
  
  // # Calculate product contribution percentage
  const calculateContribution = (productRevenue) => {
    const totalRevenue = filteredData.revenueByProduct.reduce((sum, item) => 
      sum + item.total, 0);
    return totalRevenue > 0 ? ((productRevenue / totalRevenue) * 100).toFixed(2) : 0;
  };
  
  // # Add error boundary
  if (!analyticsData?.rawPayments?.length) {
    return (
      <div className="prdct_anls_error">
        <p>No revenue data available for analysis</p>
      </div>
    );
  }

  return (
    <div className="prdct_anls_container">
      {/* Product Stats Overview */}
      <div className="prdct_anls_stats-grid">
        <div className="prdct_anls_stat-card">
          <FaShoppingCart className="prdct_anls_stat-icon" />
          <h3>Types of Products Sold</h3>
          <p className="prdct_anls_stat-value">{productStats.totalProducts}</p>
        </div>
        <div className="prdct_anls_stat-card">
          <FaMoneyBillWave className="prdct_anls_stat-icon" />
          <h3>Top Product</h3>
          <p className="prdct_anls_stat-value">{productStats.topProduct}</p>
        </div>
        <div className="prdct_anls_stat-card">
          <FaDollarSign className="prdct_anls_stat-icon" />
          <h3>Top Product Revenue</h3>
          <p className="prdct_anls_stat-value">{formatCurrency(productStats.topProductRevenue)}</p>
        </div>
        <div className="prdct_anls_stat-card">
          <FaUsers className="prdct_anls_stat-icon" />
          <h3>Number of Payments</h3>
          <p className="prdct_anls_stat-value">{productStats.totalTransactions}</p>
        </div>
      </div>
      
      {/* Product Filter Controls */}
      <div className="prdct_anls_filter-controls">
        <select 
          className="prdct_anls_filter-select"
          value={selectedProduct}
          onChange={handleProductChange}
        >
          <option value="all">All Products</option>
          {filteredData.revenueByProduct.map((product) => (
            <option key={product.product} value={product.product}>
              {product.productLabel}
            </option>
          ))}
        </select>
      </div>
      
      {/* Product Revenue Distribution */}
      <div className="prdct_anls_chart-container">
        <h3>Revenue by Product</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={filteredData.revenueByProduct}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="productLabel" />
            <YAxis />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
            <Bar dataKey="total" fill="#8884d8" name="Total Revenue (EGP)" />
            <Bar dataKey="net" fill="#82ca9d" name="Net Revenue (EGP)" />
            <Bar dataKey="budget" fill="#ffc658" name="Budget (EGP)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Product Contribution Pie Chart */}
      <div className="prdct_anls_chart-container">
        <h3>Product Revenue Contribution</h3>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={filteredData.revenueByProduct}
              dataKey="total"
              nameKey="productLabel"
              cx="50%"
              cy="50%"
              outerRadius={150}
              label={({ name, value, percent }) => 
                `${name}: ${formatCurrency(value)} (${(percent * 100).toFixed(2)}%)`
              }
            >
              {filteredData.revenueByProduct.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
      
      {/* Product Details Table */}
      <div className="prdct_anls_section">
        <h3>Product Details</h3>
        <div className="prdct_anls_table-container">
          <table className="prdct_anls_table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Total Revenue</th>
                <th>Net Revenue</th>
                <th>Budget</th>
                <th>Transactions</th>
                <th>Unique Customers</th>
                <th>Contribution</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.revenueByProduct.map((product, index) => (
                <tr key={index} className={`product-category-${product.category}`}>
                  <td>{product.productLabel}</td>
                  <td>{product.category.charAt(0).toUpperCase() + product.category.slice(1)}</td>
                  <td>{formatCurrency(product.total)}</td>
                  <td>{formatCurrency(product.net)}</td>
                  <td>{formatCurrency(product.budget)}</td>
                  <td>{product.count}</td>
                  <td>{product.customers.size}</td>
                  <td>{calculateContribution(product.total)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductAnalysis;
